import React, { useEffect, useMemo, useState } from "react";
import { getTransform, parsePresetPixelSize } from "../../../../../utils/getTransform";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useReportSubjectPlacement,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./ResizePreview.css";

const ResizePreview = ({
  imageUrl = null,
  preset = null,
  resizeMode = "fit",
  loading = false,
  applying = false,
  applyingLabel = "Applying resize…",
  emptyLabel = "Upload an image to preview",
  showSubjectBox = false,
}) => {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const stageRef = React.useRef(null);

  const parsed = useMemo(() => parsePresetPixelSize(preset), [preset]);
  const canvasAspect = useMemo(() => {
    if (!imageUrl) return 1;
    if (parsed?.aspectRatio) return parsed.aspectRatio;
    if (naturalSize.width > 0 && naturalSize.height > 0) {
      return naturalSize.width / naturalSize.height;
    }
    return 1;
  }, [parsed, naturalSize, imageUrl]);

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        canvasAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [canvasAspect, imageUrl]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height || !stageSize.width) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      resizeMode
    );
  }, [naturalSize, stageSize, resizeMode]);

  const fitFrame = useMemo(() => {
    if (!(transform.width > 0)) return null;
    return {
      x: transform.x,
      y: transform.y,
      width: transform.width,
      height: transform.height,
    };
  }, [transform.x, transform.y, transform.width, transform.height]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading && !applying;
  const [isCropping, setIsCropping] = useState(false);
  const resetKey = `${imageUrl || ""}:${resizeMode}:${parsed?.width || ""}x${parsed?.height || ""}`;
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle,
    contentCrop,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
    enabled: interactive,
    imageUrl,
    fitFrame,
    resetKey,
  });

  useReportSubjectPlacement(null, object, contentCrop, stageSize, imageUrl);

  const fitStyle = fitFrame
    ? {
        left: fitFrame.x,
        top: fitFrame.y,
        width: fitFrame.width,
        height: fitFrame.height,
      }
    : undefined;

  return (
    <ToolPrepCanvasStage
      className="resize-preview"
      ref={stageRef}
      metaImageUrl={imageUrl}
      metaWidth={parsed?.width || naturalSize.width}
      metaHeight={parsed?.height || naturalSize.height}
    >
      <div
        className="resize-preview__canvas"
        style={{
          width: stageSize.width,
          height: stageSize.height,
        }}
      >
        <div className="tool-prep-stage__clip">
          {loading ? (
            <div className="resize-preview__loading">
              <RoundLoader size="md" label="Removing background…" />
            </div>
          ) : applying ? (
            <div className="resize-preview__loading">
              <RoundLoader size="md" label={applyingLabel} />
            </div>
          ) : imageUrl && interactive && object && wrapperStyle ? (
            <div
              className="resize-preview__subject"
              style={subjectWrapperStyleForCrop(wrapperStyle, isCropping)}
            >
              <ToolPrepSubjectCropGhost
                active={isCropping}
                src={imageUrl}
                style={imageStyle}
              />
              <img
                src={imageUrl}
                alt="Resize preview"
                className="resize-preview__image"
                style={subjectMainImageStyleForCrop(imageStyle, {
                  isCropping,
                  crop,
                  originalImageSize,
                  object,
                })}
                draggable={false}
              />
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt="Resize preview"
              className={
                fitFrame
                  ? "resize-preview__image"
                  : "resize-preview__image resize-preview__image--contain"
              }
              style={fitStyle}
              draggable={false}
            />
          ) : (
            <div className="resize-preview__empty">{emptyLabel}</div>
          )}
        </div>
        {imageUrl && interactive && object && wrapperStyle ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={stageSize}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default ResizePreview;
